import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { authService } from '../../services/authService';
import AuthPage from './AuthPage';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const email = searchParams.get('email');

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(true);

  useEffect(() => {
    if (!token || !email) {
      setTokenValid(false);
      setError('Invalid or missing reset token. Please request a new password reset link.');
    }
  }, [token, email]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      setLoading(false);
      return;
    }

    try {
      await authService.resetPassword(token, formData.password, formData.confirmPassword, email);
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      const errorMessage =
        err.response?.data?.errors?.password?.[0] ||
        err.response?.data?.message ||
        'Failed to reset password. Please try again.';
      setError(errorMessage);
    }

    setLoading(false);
  };

  if (!tokenValid) {
    return (
      <AuthPage>
        <h1 className="marketing-display-md text-center text-m-ink">Invalid reset link</h1>
        <p className="marketing-body mt-3 text-center text-m-ink-muted">
          This password reset link is invalid or has expired.
        </p>
        <Link to="/forgot-password" className="marketing-btn marketing-btn-ink mt-8 w-full">
          Request a new link
        </Link>
        <p className="mt-4 text-center text-sm text-m-ink-muted">
          <Link to="/login" className="font-medium text-m-ink underline underline-offset-2">
            Back to sign in
          </Link>
        </p>
      </AuthPage>
    );
  }

  if (success) {
    return (
      <AuthPage>
        <h1 className="marketing-display-md text-center text-m-ink">Password updated</h1>
        <p className="marketing-body mt-3 text-center text-m-ink-muted">
          You can sign in with your new password. Taking you there now.
        </p>
        <Link to="/login" className="marketing-btn marketing-btn-ink mt-8 w-full">
          Sign in
        </Link>
      </AuthPage>
    );
  }

  return (
    <AuthPage>
      <h1 className="marketing-display-md text-center text-m-ink">Reset password</h1>
      <p className="marketing-body mt-2 text-center text-m-ink-muted">
        Choose a new password for your account.
      </p>

      <form className="mt-8 space-y-3" onSubmit={handleSubmit}>
        {error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
            {error}
          </div>
        )}
        <div className="relative">
          <input
            id="password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            required
            value={formData.password}
            onChange={handleChange}
            placeholder="New password"
            className="auth-input pr-11"
          />
          <button
            type="button"
            className="absolute inset-y-0 right-0 flex items-center pr-4 text-m-ink-faint hover:text-m-ink"
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        <div className="relative">
          <input
            id="confirmPassword"
            name="confirmPassword"
            type={showConfirmPassword ? 'text' : 'password'}
            autoComplete="new-password"
            required
            value={formData.confirmPassword}
            onChange={handleChange}
            placeholder="Confirm password"
            className="auth-input pr-11"
          />
          <button
            type="button"
            className="absolute inset-y-0 right-0 flex items-center pr-4 text-m-ink-faint hover:text-m-ink"
            onClick={() => setShowConfirmPassword((v) => !v)}
            aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
          >
            {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        <p className="text-xs text-m-ink-muted">Use at least 8 characters.</p>
        <button
          type="submit"
          disabled={loading}
          className="marketing-btn marketing-btn-ink w-full"
        >
          {loading ? 'Saving…' : 'Reset password'}
        </button>
        <p className="text-center text-sm text-m-ink-muted">
          <Link to="/login" className="font-medium text-m-ink underline underline-offset-2">
            Back to sign in
          </Link>
        </p>
      </form>
    </AuthPage>
  );
};

export default ResetPassword;
